import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { usePageContextQuery } from "@/shared/page-context";
import { getUserPreferences } from "./getUserPreferences";
import { mergePayloadIntoPreferences } from "./mergePayloadIntoPreferences";
import { saveSearchPreferences } from "./saveSearchPreferences";
import type { SaveUserPreferencesPayload, UserPreferences } from "./types";

export const userPreferencesQueryKey = ["user-preferences"];

export const userPreferencesQueryOptions = queryOptions({
    queryKey: userPreferencesQueryKey,
    queryFn: () => getUserPreferences().catch(() => null),
});

export const useUserPreferencesQuery = () => {
    return useSuspenseQuery(userPreferencesQueryOptions);
};

export const useSaveUserPreferencesMutation = () => {
    const { data: pageContext } = usePageContextQuery();
    const queryClient = useQueryClient();

    // Prevent saving data for institutional users
    return useMutation({
        mutationFn: (data: SaveUserPreferencesPayload) => {
            if (pageContext.isInstitutional) return Promise.resolve();
            return saveSearchPreferences(pageContext.partnerId, data);
        },
        onMutate: (data) => {
            // Optimistic update: merge submitted preferences into the cache so that
            // bfcache-restored pages see the freshly-saved values (invalidation is
            // unreliable after bfcache restore since no refetch is triggered).
            const cached = queryClient.getQueryData<UserPreferences | null>(userPreferencesQueryKey);

            // When cached value is null (institutional user or failed initial fetch),
            // skip the optimistic write — there's no baseline to merge into.
            if (cached == null) return;

            queryClient.setQueryData(userPreferencesQueryKey, mergePayloadIntoPreferences(cached, data));
        },
    });
};
